import { useEffect, ReactNode } from 'react';
import { Badge } from 'antd';
import apiRequests from 'utils/api';
import apiRoutes from 'config/apiRoute';
import useWebSocket from 'utils/useWebSocket';
import useLiveCounter from 'utils/useLiveCounter';
import { PENDING_REPLIES_INTERACTED_EVENT } from 'utils/counterEvents';
import { PendingResponseCountData } from 'modules/pending-replies/types';

interface CountPendingBadgeProps {
  children?: ReactNode;
  collapsed?: boolean;
}

const fetchPendingCounts = async (abortController: AbortController): Promise<PendingResponseCountData> => {
  const response = await apiRequests.get(apiRoutes.PENDING_RESPONSES_COUNT, {}, {}, abortController, true);

  return response.data.data;
};

const CountPendingBadge = ({ children, collapsed }: CountPendingBadgeProps) => {
  const { value: counts, refresh } = useLiveCounter<PendingResponseCountData>({ fetcher: fetchPendingCounts });

  const totalPending = (counts?.owned ?? 0) + (counts?.following ?? 0);

  useEffect(() => {
    window.addEventListener(PENDING_REPLIES_INTERACTED_EVENT, refresh);

    return () => {
      window.removeEventListener(PENDING_REPLIES_INTERACTED_EVENT, refresh);
    };
  }, [refresh]);

  // A pending response is cleared by a reply, a reaction or by the request itself moving on,
  // so all three have to invalidate the counter. `refresh` coalesces the bursts.
  useWebSocket({
    channelName: 'model.changes',
    listen: [
      {
        event: '.app.models.foundation.comment',
        callback: (event: any) => {
          // Pending responses only exist on requests - task and project comments are noise here.
          if (event?.payload?.request_id == null) return;

          refresh();
        },
      },
      { event: '.app.models.foundation.reaction', callback: refresh },
      { event: '.app.models.projects.request', callback: refresh },
    ],
  });

  if (totalPending === 0) return children ? <>{children}</> : null;

  if (collapsed) {
    return (
      <Badge count={totalPending} size="small" title="" style={{ position: 'absolute', right: '-20px', bottom: '0px' }} />
    );
  }

  if (children) {
    return (
      <Badge count={totalPending} size="small" title="" offset={[-5, 5]}>
        {children}
      </Badge>
    );
  }

  return <Badge count={totalPending} title="" />;
};

export default CountPendingBadge;
